import type { UpdateChatInput } from '../../../../generated/graphql-manager';

import {
  Heading,
  Paragraph,
  Radio,
  SelectionInputLayout,
  Toggle,
  classNames,
} from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { InfoTooltip } from 'src/components/info-tooltip';

import {
  ChatType,
  State,
  useCreateChatMutation,
  useDeleteChatMutation,
  useGetChatTypeQuery,
  useUpdateChatMutation,
} from '../../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../../routes';
import { SetupPageFooter } from '../../../components/setup-page-footer';
import { useUserPermissions } from '../../../hooks/user-permissions';
import managerStyles from '../../../manager.module.css';

import styles from './messaging.module.css';

export function MessagingPage() {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const routes = useAbsoluteRoutes();
  const { isEventEditingAllowed } = useUserPermissions();

  const getChatTypeQueryResponse = useGetChatTypeQuery({ variables: { webcastId } });
  const formDisabled = getChatTypeQueryResponse.data?.webcast?.state !== State.PRELIVE || !isEventEditingAllowed;
  const loading = getChatTypeQueryResponse.loading;

  const chatSettings = getChatTypeQueryResponse.data?.webcast?.chat?.settings;
  const chatType = chatSettings?.type;
  const useProvidedUsername =
    chatSettings && 'useProvidedUsername' in chatSettings ? chatSettings.useProvidedUsername : undefined;

  const [createChatMutation, { loading: createChatTypeLoading }] = useCreateChatMutation();

  const createChat = (type: ChatType) => {
    createChatMutation({
      variables: { input: { webcastId, type } },
      optimisticResponse: { createChat: { __typename: 'CreateChatSuccess' } },
      update: (cache, { data }) => {
        if (data?.createChat.__typename !== 'CreateChatSuccess') return;

        const __typename = {
          [ChatType.UNMODERATED]: 'UnmoderatedChatSettings',
          [ChatType.MODERATED]: 'ModeratedChatSettings',
          [ChatType.ANONYMOUS]: 'AnonymousChatSettings',
        }[type];

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: {
            chat: () => ({
              __typename: 'Chat',
              settings: { __typename, type, useProvidedUsername: true },
            }),
          },
        });
      },
    });
  };

  const [updateChatMutation, { loading: updateChatLoading }] = useUpdateChatMutation();

  const updateChat = (input: UpdateChatInput) => {
    updateChatMutation({
      variables: { input },
      optimisticResponse: { updateChat: { __typename: 'UpdateChatSuccess' } },
      update: (cache, { data }) => {
        if (data?.updateChat.__typename !== 'UpdateChatSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: { chat: (chat) => ({ ...chat, settings: { ...chat.settings, ...input } }) },
        });
      },
    });
  };

  const [deleteChatMutation, { loading: deleteChatTypeLoading }] = useDeleteChatMutation();

  const deleteChat = () => {
    deleteChatMutation({
      variables: { webcastId },
      optimisticResponse: { deleteChat: { __typename: 'DeleteChatSuccess' } },
      update: (cache, { data }) => {
        if (data?.deleteChat.__typename !== 'DeleteChatSuccess') return;

        cache.modify({
          id: cache.identify({ __typename: 'Webcast', id: webcastId }),
          fields: { chat: () => null },
        });
      },
    });
  };

  const handleChangeChatType = (newType?: ChatType) => () => {
    if (newType)
      if (chatType) updateChat({ webcastId, type: newType });
      else createChat(newType);
    else deleteChat();
  };

  return (
    <main className={classNames(managerStyles.main, styles.wrapper)} data-testid="messaging-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.messaging.title')}</Heading>
      <Paragraph className={managerStyles.grayText}>{t('manager.webcastSetup.messaging.description')}</Paragraph>

      <div className={styles.options}>
        <SelectionInputLayout
          checked={chatType === undefined}
          disabled={formDisabled || loading}
          inputElement={
            <Radio
              data-testid="disabled-chat-option"
              name="chat-type"
              value={undefined}
              checked={chatType === undefined}
              disabled={formDisabled || loading}
              onChange={handleChangeChatType(undefined)}
            >
              {t('manager.webcastSetup.messaging.options.disabledChat.label')}
            </Radio>
          }
        />

        <fieldset className={styles.fieldset} disabled={formDisabled}>
          <SelectionInputLayout
            hintMessage={t('manager.webcastSetup.messaging.options.unmoderatedChat.description')}
            checked={chatType === ChatType.UNMODERATED}
            disabled={formDisabled || loading}
            inputElement={
              <Radio
                data-testid="unmoderated-chat-option"
                name="chat-type"
                value={ChatType.UNMODERATED}
                checked={chatType === ChatType.UNMODERATED}
                disabled={formDisabled || loading}
                onChange={handleChangeChatType(ChatType.UNMODERATED)}
              >
                {t('manager.webcastSetup.messaging.options.unmoderatedChat.label')}
              </Radio>
            }
          >
            {chatType === ChatType.UNMODERATED && (
              <div className={classNames(styles.expandableSection, formDisabled && styles.disabled)}>
                <div className={styles.expandableSectionOption}>
                  <span className={styles.expandableSectionLabel}>
                    {t('manager.webcastSetup.messaging.features.useProvidedUsername.label')}

                    <InfoTooltip label={t('manager.webcastSetup.messaging.features.useProvidedUsername.tooltip')} />
                  </span>

                  <Toggle
                    data-testid="unmoderated-chat-option-use-provided-username-toggle"
                    checked={useProvidedUsername}
                    onChange={() => updateChat({ webcastId, useProvidedUsername: !useProvidedUsername })}
                  />
                </div>

                <span className={styles.expandableSectionDescription}>
                  {t('manager.webcastSetup.messaging.features.useProvidedUsername.description')}
                </span>
              </div>
            )}
          </SelectionInputLayout>
        </fieldset>

        <fieldset className={styles.fieldset} disabled={formDisabled}>
          <SelectionInputLayout
            hintMessage={t('manager.webcastSetup.messaging.options.moderatedChat.description')}
            checked={chatType === ChatType.MODERATED}
            disabled={formDisabled || loading}
            inputElement={
              <Radio
                data-testid="moderated-chat-option"
                name="chat-type"
                value={ChatType.MODERATED}
                checked={chatType === ChatType.MODERATED}
                disabled={formDisabled || loading}
                onChange={handleChangeChatType(ChatType.MODERATED)}
              >
                {t('manager.webcastSetup.messaging.options.moderatedChat.label')}
              </Radio>
            }
          >
            {chatType === ChatType.MODERATED && (
              <div className={classNames(styles.expandableSection, formDisabled && styles.disabled)}>
                <div className={styles.expandableSectionOption}>
                  <span className={styles.expandableSectionLabel}>
                    {t('manager.webcastSetup.messaging.features.useProvidedUsername.label')}

                    <InfoTooltip
                      label={t('manager.webcastSetup.messaging.features.useProvidedUsername.tooltip')}
                      className={styles.hint}
                    />
                  </span>

                  <Toggle
                    data-testid="moderated-chat-option-use-provided-username-toggle"
                    checked={useProvidedUsername}
                    onChange={() => updateChat({ webcastId, useProvidedUsername: !useProvidedUsername })}
                  />
                </div>

                <span className={styles.expandableSectionDescription}>
                  {t('manager.webcastSetup.messaging.features.useProvidedUsername.description')}
                </span>
              </div>
            )}
          </SelectionInputLayout>
        </fieldset>
      </div>

      <SetupPageFooter
        nextLabel={t('manager.webcastSetup.messaging.nextStep.label')}
        route={routes.webcastSetup_eventRecording}
        saving={createChatTypeLoading || updateChatLoading || deleteChatTypeLoading}
      />
    </main>
  );
}
